import { CheckCircleIcon, MinusCircleIcon, ProhibitIcon, XCircleIcon } from '@phosphor-icons/react'
import { match } from 'ts-pattern'
import { z } from 'zod'
import { Spinner } from '../spinner.tsx'
import * as styles from './activity-step-card.css.ts'
import { ActivityWidget, type ActivityWidgetItem } from './activity-widget.tsx'
import { CveBrowserFacsimile } from './cve-browser-facsimile.tsx'

export const ActivityStepStatusSchema = z.enum(['in_progress', 'completed', 'error', 'blocked', 'excluded'])
export type ActivityStepStatus = z.infer<typeof ActivityStepStatusSchema>

/**
 * Human-readable status text for assistive tech. The raw enum reads as an internal identifier
 * ("in_progress"), which is what a title-less card would otherwise announce as its whole label.
 */
const STATUS_LABELS: Record<ActivityStepStatus, string> = {
  in_progress: 'In progress',
  completed: 'Completed',
  error: 'Error',
  blocked: 'Blocked',
  excluded: 'Excluded',
}

export type ActivityStepCardProps = {
  /** Absent for a card whose whole body is a widget, which carries no title of its own. */
  title?: string
  description?: string
  status: ActivityStepStatus
  /** Widgets nested inside this card's body, folded from their snapshot events. */
  widgets?: ActivityWidgetItem[]
}

export const ActivityStepCard: React.FC<ActivityStepCardProps> = ({ title, description, status, widgets }) => {
  const icon = match(status)
    .with('in_progress', () => (
      <div className={styles.spinnerContainer} aria-label={STATUS_LABELS.in_progress} role="status">
        <Spinner size="24" variant="cool" label={title ? `${title} in progress` : 'In progress'} />
      </div>
    ))
    .with('completed', () => (
      <div className={styles.iconContainer({ status })} aria-label={STATUS_LABELS.completed} role="status">
        <CheckCircleIcon size={24} weight="light" />
      </div>
    ))
    .with('error', () => (
      <div className={styles.iconContainer({ status })} aria-label={STATUS_LABELS.error} role="status">
        <XCircleIcon size={24} weight="light" />
      </div>
    ))
    .with('blocked', () => (
      <div className={styles.iconContainer({ status })} aria-label={STATUS_LABELS.blocked} role="status">
        <ProhibitIcon size={24} weight="light" />
      </div>
    ))
    .with('excluded', () => (
      <div className={styles.iconContainer({ status })} aria-label={STATUS_LABELS.excluded} role="status">
        <MinusCircleIcon size={24} weight="light" />
      </div>
    ))
    .exhaustive()

  const cveMatch = status === 'in_progress' ? title?.match(/^Researching (CVE-\d+-\d+)$/) : undefined
  const researchingCveId = cveMatch?.[1]

  // "Researching CVE-XXXX in <package> [<ecosystem>] to build a verification plan"
  const descMatch =
    researchingCveId && description
      ? description.match(/^Researching CVE-[\d-]+ in (.+?)(?:\s+\[([a-z]+)\])? to build a verification plan$/)
      : null
  const packageName = descMatch?.[1] ?? undefined
  const ecosystem = descMatch?.[2] ?? undefined

  return (
    <div
      className={styles.card({ status })}
      role="listitem"
      aria-label={title ? `${title}: ${STATUS_LABELS[status]}` : STATUS_LABELS[status]}
    >
      {icon}

      <div className={styles.content}>
        {title && <div className={styles.title}>{title}</div>}
        {description && <div className={styles.description}>{description}</div>}
        {widgets?.map(widget => (
          <ActivityWidget key={widget.id} payload={widget.payload} />
        ))}
        {researchingCveId && (
          <CveBrowserFacsimile cveId={researchingCveId} packageName={packageName} ecosystem={ecosystem} />
        )}
      </div>
    </div>
  )
}
